import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { HourglassMedium, ArrowsClockwise, Warning } from "@phosphor-icons/react";
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Cell } from "recharts";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useOpsProjects } from "@/components/OpsProjectSelector";

const firstOfMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`; };
const today = () => new Date().toISOString().slice(0, 10);
const fmtH = (h) => `${Math.floor(h || 0)}h${String(Math.round(((h || 0) % 1) * 60)).padStart(2, "0")}m`;
const SHIFT_L = { dia: "Diurno", noche: "Nocturno" };
const BARS = ["#1e3a8a", "#2563eb", "#3b82f6", "#60a5fa", "#93c5fd", "#b91c1c", "#f59e0b", "#64748b"];

export default function OperationsDelays() {
  const projects = useOpsProjects();
  const projName = useCallback((id) => projects.find((p) => p.id === id)?.name || (id ? "Proyecto" : "—"), [projects]);
  const [range, setRange] = useState({ date_from: firstOfMonth(), date_to: today() });
  const [groupBy, setGroupBy] = useState("motivo");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/operations/delays", { params: { ...range, group_by: groupBy } }); setData(r.data); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, [range, groupBy]);
  useEffect(() => { load(); }, [load]);

  const chartData = useMemo(
    () => (data?.pareto || []).slice(0, 15).map((b) => ({ name: b.key, hours: b.hours, cum: b.cum_pct, events: b.events })),
    [data],
  );

  return (
    <div className="space-y-4" data-testid="ops-delays">
      <div className="flex flex-wrap items-end gap-2 bg-slate-50 border border-slate-200 rounded-lg p-3">
        <div className="flex items-center gap-1.5 mr-2">
          <HourglassMedium size={20} weight="duotone" className="text-blue-900" />
          <div>
            <p className="font-heading font-black text-sm text-slate-900 leading-tight">Demoras</p>
            <p className="text-[10px] text-slate-500">Capturadas desde Mi Turno</p>
          </div>
        </div>
        <div className="flex rounded-md overflow-hidden border border-blue-900" data-testid="ops-delays-group">
          {[["motivo", "Por motivo"], ["equipo", "Por equipo"]].map(([g, l]) => (
            <button key={g} onClick={() => setGroupBy(g)} className={`h-8 px-3 text-xs font-bold ${groupBy === g ? "bg-blue-900 text-white" : "bg-white text-blue-900"}`} data-testid={`ops-delays-group-${g}`}>{l}</button>
          ))}
        </div>
        <div><Label className="text-[10px] uppercase">Desde</Label><Input type="date" value={range.date_from} onChange={(e) => setRange({ ...range, date_from: e.target.value })} className="h-8" data-testid="ops-delays-from" /></div>
        <div><Label className="text-[10px] uppercase">Hasta</Label><Input type="date" value={range.date_to} onChange={(e) => setRange({ ...range, date_to: e.target.value })} className="h-8" data-testid="ops-delays-to" /></div>
        <Button size="sm" variant="outline" onClick={load} className="h-8 gap-1" data-testid="ops-delays-refresh"><ArrowsClockwise size={13} /> Actualizar</Button>
        {data && <span className="text-[11px] text-slate-500 ml-auto" data-testid="ops-delays-total">{data.total_events} demora(s) · {fmtH(data.total_hours)} perdidas</span>}
      </div>

      {loading && !data && <p className="text-center text-slate-400 text-sm py-10">Calculando…</p>}

      {data && (
        <>
          <div className="bg-white border border-slate-200 rounded-lg p-3" data-testid="ops-delays-pareto">
            <h3 className="font-heading font-bold text-blue-900 uppercase text-[11px] tracking-widest mb-2">
              Pareto de demoras · {groupBy === "equipo" ? "por equipo" : "por motivo"} (horas perdidas)
            </h3>
            {chartData.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-8" data-testid="ops-delays-pareto-empty">Sin demoras en el período.</p>
            ) : (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData} margin={{ top: 8, right: 36, left: 0, bottom: 56 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 9 }} angle={-30} textAnchor="end" interval={0} height={60} />
                    <YAxis yAxisId="h" tick={{ fontSize: 9 }} label={{ value: "Horas", angle: -90, position: "insideLeft", style: { fontSize: 9 } }} />
                    <YAxis yAxisId="c" orientation="right" domain={[0, 100]} tick={{ fontSize: 9 }} unit="%" />
                    <Tooltip contentStyle={{ fontSize: 11 }} formatter={(v, n) => n === "cum" ? [`${v}%`, "Acumulado"] : n === "hours" ? [fmtH(v), "Horas"] : [v, n]} />
                    <Bar yAxisId="h" dataKey="hours" name="Horas" radius={[3, 3, 0, 0]}>
                      {chartData.map((_, i) => <Cell key={i} fill={BARS[i % BARS.length]} />)}
                    </Bar>
                    <Line yAxisId="c" type="monotone" dataKey="cum" name="Acumulado" stroke="#b91c1c" strokeWidth={2} dot={{ r: 2 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-3" data-testid="ops-delays-table-wrap">
            <h3 className="font-heading font-bold text-blue-900 uppercase text-[11px] tracking-widest mb-2">Detalle de demoras ({data.items.length})</h3>
            <div className="max-h-96 overflow-auto border rounded">
              <table className="w-full text-[11px]" data-testid="ops-delays-table">
                <thead className="bg-slate-100 sticky top-0">
                  <tr>{["Fecha", "Turno", "Equipo", "Código", "Operador", "Área", "Proyecto", "Inicio", "Fin", "Duración", "Motivo"].map((h) => <th key={h} className="px-2 py-1 text-left font-semibold whitespace-nowrap">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {data.items.map((it, i) => (
                    <tr key={i} className="border-t hover:bg-slate-50" data-testid={`ops-delays-row-${i}`}>
                      <td className="px-2 py-1 whitespace-nowrap">{it.date}</td>
                      <td className="px-2 py-1">{SHIFT_L[it.shift] || it.shift}</td>
                      <td className="px-2 py-1">{it.equipment_label}</td>
                      <td className="px-2 py-1 font-mono">{it.equipment_code || "—"}</td>
                      <td className="px-2 py-1">{it.operator_name || "—"}</td>
                      <td className="px-2 py-1">{it.area || "—"}</td>
                      <td className="px-2 py-1">{projName(it.project_id)}</td>
                      <td className="px-2 py-1 font-mono">{it.inicio || "—"}</td>
                      <td className="px-2 py-1 font-mono">{it.fin || "—"}</td>
                      <td className="px-2 py-1 font-bold whitespace-nowrap">{fmtH(it.duration_h)}</td>
                      <td className="px-2 py-1 text-slate-700">{it.motivo || <span className="text-slate-400 italic">(sin motivo)</span>}</td>
                    </tr>
                  ))}
                  {data.items.length === 0 && <tr><td colSpan={11} className="text-center text-slate-400 py-6"><Warning size={14} className="inline mr-1" /> Sin demoras registradas en el período.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
